import React, {useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {formatLikeCount, movieTitleHandler} from "../../../utils/helpers";
import { ROUTES } from "../../../constants/routes";
import { genresData } from "../../../constants/structure";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeIcon from "../../../components/icons/chat/likeIcon";
import ShareIcon from "../../../components/icons/chat/shareIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import styles from "./MovieCarousel.module.scss";
import RoomVideoPlayer from "../../room/components/RoomVideoPlayer";
import type {Swiper as SwiperClass} from "swiper";

const formatRating = (rating?: number | null) => (rating != null ? rating.toFixed(1) : undefined);

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  trailer_key?: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
  liked: boolean;
  in_favorite: boolean;
  shares_count: number;
  favorites_count: number;
}

interface CarouselProps {
  movies: Movie[];
  muted: boolean;
  onToggleMuted: () => void;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  likesList: number[];
  favoritesList: number[];
  updateVoteLocal: (id: any, key: string, type: string) => void;
  isMobile: boolean;
  openLoginModal: () => void;
  onShare: (id: number) => void;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const MovieCarouselMobile: React.FC<CarouselProps> = ({
                                                        movies,
                                                        muted,
                                                        onToggleMuted,
                                                        likeHandler,
                                                        favouritesHandler,
                                                        likesList,
                                                        favoritesList,
                                                        openLoginModal,
                                                        onShare,
                                                      }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const mainSwiperRef = useRef<SwiperClass | null>(null);
  const thumbsSwiperRef = useRef<SwiperClass | null>(null);

  const syncSliders = (index: number) => {
    setActiveIndex(index);
    if (mainSwiperRef.current && mainSwiperRef.current.activeIndex !== index) {
      mainSwiperRef.current.slideTo(index);
    }
    if (thumbsSwiperRef.current && thumbsSwiperRef.current.activeIndex !== index) {
      thumbsSwiperRef.current.slideTo(index);
    }
  };

  const handleNavigateUp = () => {
    if (activeIndex > 0) {
      syncSliders(activeIndex - 1);
    }
  };

  const handleNavigateDown = () => {
    if (activeIndex < movies.length - 1) {
      syncSliders(activeIndex + 1);
    }
  };


  return (
    <div className={styles.movieCarouselWrap}>
      <Swiper
        modules={[Pagination]}
        slidesPerView={1}
        spaceBetween={20}
        pagination={{ clickable: true }}
        className={styles.movieSwiper}
        onSlideChange={(swiper: SwiperClass) => syncSliders(swiper.activeIndex)}
      >
        {movies.map((movie, index) => {
          const title = movieTitleHandler(i18n.language, movie) || movie.title;
          const year = movie.release_date?.substring(0, 4);
          const genres = movie.genre_ids
            ?.slice(0, 2)
            .map((gid) => genresData.find((g) => g.id === gid)?.name)
            .filter(Boolean)
            .map((name) => t(name!))
            .join(", ");

          const primaryRating = formatRating(movie.imdb_rating ?? movie.vote_average) || "—";
          const secondaryRating = formatRating(movie.rotten_tomatoes_rating ?? movie.imdb_rating ?? movie.vote_average) || primaryRating;
          const liked = likesList?.includes(movie.id);
          const isFav = favoritesList?.includes(movie.id);

          return (
            <SwiperSlide key={movie.id} onClick={() => navigate(`${ROUTES.DETAILS}/${movie.id}`)}>
              <div className={styles.movieCarouselSlide}>
                <div className={styles.movieCarouselCard}>
                  <div className={styles.movieCarouselVideo}>
                    <RoomVideoPlayer
                      trailerKey={movie.trailer_key ?? null}
                      backdropPath={movie.backdrop_path}
                      muted={muted}
                      onToggleMuted={onToggleMuted}
                      isActive={activeIndex === index}
                      navigation={{
                        mode: "slides",
                        onNavigateUp: handleNavigateUp,
                        onNavigateDown: handleNavigateDown,
                        canNavigateUp: activeIndex > 0,
                        canNavigateDown: activeIndex < movies.length - 1,
                      }}
                    />
                  </div>
                  <div className={styles.movieCarouselInfo}>
                    <div className={styles.movieCarouselBlock}>
                      {movie.poster_path ? (
                        <div className={styles.movieCarouselImage}>
                          <img src={`${IMG_BASE}${movie.poster_path}`} alt={title} />
                        </div>
                      ) : (
                        <div className={styles.movieCarouselPosterPlaceholderSmall} />
                      )}

                      <div className={styles.movieCarouselBox}>
                        <h4 className={styles.movieCarouselSlideTitle}>{title}</h4>
                        <div className={styles.movieCarouselSlideMeta}>
                          {genres && <span className={styles.genres}>{genres}</span>}
                          {year && <span className={styles.year}>{year}</span>}
                        </div>
                        <div className={styles.ratings}>
                          <div className={styles.ratingItem}>
                            <OverlayRatingIcon />
                            <span className={styles.rating}>{primaryRating}</span>
                          </div>
                          <div className={styles.ratingItem}>
                            <OverlayTomatoRatingIcon />
                            <span className={styles.rating}>{secondaryRating}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.movieCarouselActions}>
                      <div
                        id="likesDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) {
                            openLoginModal();
                            return;
                          }
                          likeHandler(e, movie, liked);
                        }}
                      >
                        <div className={styles.movieCarouselActionsIcon}>
                          <LikeIcon />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{movie.vote_count}</div>
                      </div>

                      <div
                        id="savedDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) {
                            openLoginModal();
                            return;
                          }
                          favouritesHandler(e, movie, isFav);
                        }}
                      >
                        <div className={styles.movieCarouselActionsIcon}>
                          <BookMarkIcon />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.favorites_count, { compact: true })}</div>
                      </div>

                      <div
                        id="shareDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onShare(movie.id);
                        }}
                      >
                        <div className={styles.movieCarouselActionsIcon}>
                          <ShareIcon />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.shares_count, { compact: true })}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default MovieCarouselMobile;
